/* eslint-disable react-native/no-inline-styles */
import React, {useEffect, useRef, useState} from 'react';
import Box from '@/Atoms/Box';
import useGetUserGamesApi from '@/Hooks/GetUserGamesApi/useGetUserGamesApi';
import {useNavigation, useRoute} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import {ScreenName} from '@/navigators/StackConstants';
import CCButton from '@/Atoms/CCButton';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import CCColors from '@/Utils/CCColors';
import pullGameStatusApi from '@/Hooks/PullIncrementalGamesApi/pullGameStatusApi';
import usePullGamesApi from '@/Hooks/PullIncrementalGamesApi/pullIncrementalGamesApi';
import useMarkFavouriteGameApi from '@/Hooks/MarkFavouriteGameApi/markFavouriteGameApi';
import AntDesign from 'react-native-vector-icons/AntDesign';
import {useFocusEffect} from '@react-navigation/native';
import {
  View,
  ActivityIndicator,
  Pressable,
  TextInput,
  Image,
  ScrollView,
} from 'react-native';
import CCLoader from '@/Atoms/CCLoader';
import useGAPageView from '@/navigators/useGAPageView';
import customStyles from './styles';
import Pagination from './Pagination';
import Filters from './components/Filters';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import Spacer from '@/Atoms/Spacer';
import ConditionalRender from '@/Atoms/ConditionalRender';
import {capitalizeFirstLetter, debounce} from '@/Utils/CommonUtils';
import SubscriptionModal from '../Subscription/components/Modals';
import search from '../../Assets/Images/png/search.png';
import CCTextInput from '@/Atoms/CCTextInput';
import useAlertModalStore from '@/Store/AlertModalStore';
import {useNonPersistedUserPlanStore} from '@/Store/userPlanStore';
import {FavStar} from './components/FavStar';
import {GameCard} from './components/GameCard';
import {convertTimeControlToMins} from '../ChessAnalysis/utils';

export {FavStar};

// near the top of Games/index.tsx
const ROUTE_FILTER_KEYS = [
  'filter_date',
  'assessmentId',
  'opening_mainline',
  'opening',
  'endgame_type',
] as const;

const pickRouteFilters = (source: Record<string, any>) =>
  ROUTE_FILTER_KEYS.reduce((acc, key) => {
    acc[key] = source?.[key];
    return acc;
  }, {} as Record<string, any>);

// ─── Main Games Component ────────────────────────────────────────────────────
const Games: React.FC = ({dashboardData}) => {
  const {isMobileOrTab} = useMediaQuery();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<'ALL' | 'FAVORITES'>('ALL');
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [import_in_progress, setImportInProgress] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [isSubscriptionModalVisible, setIsSubscriptionModalVisible] =
    useState(false);
  const [limitType, setLimitType] = useState('');
  const [usageLimit, setUsageLimit] = useState(0);
  const [planName, setPlanName] = useState('');
  const {subscriptionModalOpen, setSubscriptionModalOpen} =
    useNonPersistedUserPlanStore();
  const [filterText, setFilterText] = useState<string>('');
  const openAlertModal = useAlertModalStore(state => state.openModal);
  const [favouriteState, setFavouriteState] = useState<Record<string, string>>(
    {},
  );

  const handleOnGetUserSuccess = (data: any) => {
    setLimitType(data?.limit_type);
    setUsageLimit(data?.usage_limit);
    setPlanName(data?.plan_name);
    if (data?.data?.is_permitted === false) {
      setSubscriptionModalOpen(true);
    }
  };

  const handleOnGetUserError = (error: any) => {
    console.log('handleOnGetUserError', error);
  };

  const {
    mutate: fetchGames,
    data,
    isLoading,
  } = useGetUserGamesApi(handleOnGetUserSuccess, handleOnGetUserError);

  const updateFavouriteStatus = (game_uuid: string, value: number) => {
    const val = value === 1 ? '⭐' : '☆';
    setFavouriteState(prev => ({...prev, [game_uuid]: val}));
  };

  const handleBookMarkUpdateSuccess = (responseData: any) => {
    updateFavouriteStatus(
      responseData.data.game_uuid,
      responseData.data.result,
    );
  };

  const handleBookMarkUpdateFailure = (responseData: any) => {
    updateFavouriteStatus(
      responseData.data.game_uuid,
      responseData.data.result,
    );
  };

  const handleFilter = (newFilters: any) => {
    const queryParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    setFilters(newFilters);
    setCurrentPage(1);
    const updatedFilters = {...newFilters, page: 1};
    navigation.setParams({
      ...pickRouteFilters(updatedFilters),
      query: queryString,
    });
    fetchGames(updatedFilters);
  };

  const timer: any = useRef(null);

  const handleSuccess = (apiData: any) => {
    setCount(c => c + 1);
    if (count >= 25) {
      setImportInProgress(false);
      clearInterval(timer.current);
      fetchGames({page: 1});
    } else if (apiData.data.status === 'COMPLETED') {
      setImportInProgress(false);
      clearInterval(timer.current);
      fetchGames({page: 1});
    } else {
      const {imported_games, total_games} = apiData.data.data;
      setProgress(imported_games / total_games);
    }
  };

  const {mutate: pullGamesStatus} = pullGameStatusApi(handleSuccess, () => {});
  const {mutate: pullGames} = usePullGamesApi(
    () => {
      setImportInProgress(true);
      timer.current = setInterval(pullGamesStatus, 1000);
    },
    () => {},
  );
  const {mutate: markFavourite} = useMarkFavouriteGameApi(
    handleBookMarkUpdateSuccess,
    handleBookMarkUpdateFailure,
  );

  const navigation = useNavigation();
  const [filter_date, setFilterDate] = useState<string>('');
  const route = useRoute();

  function decodeAndSplit(input: string) {
    return input
      .split('%2C')
      .map(item => decodeURIComponent(item.replace(/\+/g, ' ')));
  }

  useFocusEffect(
    React.useCallback(() => {
      setFilterDate(() => {
        const date = route.params?.filter_date;
        const queryString = route?.params?.query;
        const obj: Record<string, any> = {};
        if (queryString) {
          queryString?.split('&')?.forEach((item: string) => {
            const parts = item?.split('=');
            obj[parts[0]] = decodeAndSplit([parts[1]].toString());
          });
        }
        fetchGames({page: 1, ...pickRouteFilters(route.params), ...obj});
        return date;
      });
    }, [route.params?.filter_date, route.params?.assessmentId]),
  );

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchGames({
        page: pageNumber,
        ...filters,
        ...pickRouteFilters(route.params),
      });
    }
  };

  useEffect(() => {
    if (data && Array.isArray(data?.data?.data)) {
      setTotalPages(data.data.total_pages);
    }
  }, [data, currentPage]);

  const callHandleFilter = debounce(
    (newFilters: any) => handleFilter(newFilters),
    1000,
  );

  const filterSearchHandler = (text: string) => {
    setFilterText(text);
    callHandleFilter({...filters, player_name: text});
  };

  function modalFunctions() {
    setImportInProgress(true);
    pullGames();
  }

  const planMessage = `${limitType} limit of ${usageLimit} ${
    usageLimit === 1 ? 'game review' : 'game reviews'
  } is exhausted. Upgrade now to continue with the game analysis.`;

  if (isLoading) {
    return <CCLoader loading bgWhite />;
  }

  const games = data?.data?.data ?? [];

  return (
    <>
      <View style={customStyles.pageRoot}>
        {/* ── Dark Premium Hero Banner ──────────────────────────── */}
        <View style={customStyles.heroBannerContainer}>
          <View style={customStyles.heroBannerCard}>
            {/* Teal-to-gold top accent line */}
            <View style={customStyles.heroBannerAccentLine} />

            {/* Main content row: left copy + right CTA */}
            <View style={customStyles.heroBannerContent}>
              <View style={customStyles.heroBannerLeft}>
                <CCText style={customStyles.heroBannerLabel}>
                  Analysis Dashboard
                </CCText>
                <CCText style={customStyles.heroBannerTitle}>
                  Game{' '}
                  <CCText
                    style={[
                      customStyles.heroBannerTitle,
                      customStyles.heroBannerTitleAccent,
                    ]}>
                    Reviews
                  </CCText>
                </CCText>
                <CCText style={customStyles.heroBannerSubtitle}>
                  Analyze your games, identify critical moments, and turn your
                  mistakes into custom puzzles.
                </CCText>
              </View>

              <View style={customStyles.heroBannerRight}>
                <CCButton
                  disabled={import_in_progress}
                  onPress={() => {
                    if (
                      dashboardData?.user?.cdc_name == null &&
                      dashboardData?.user?.lic_name == null
                    ) {
                      openAlertModal({
                        header: 'Info',
                        bodyTxt:
                          'You need to provide a lichess or chess.com ID in the user profile page to continue with the import.',
                        buttonText: 'OK',
                      });
                    } else {
                      openAlertModal({
                        header: 'Info',
                        bodyTxt:
                          '1. Games played on lichess.org and chess.com can only be imported after 15 mins once the game ends.\n\n2. Imports will run in the background, this may take a while.',
                        buttonText: 'Continue',
                        handleConfirmation: modalFunctions,
                      });
                    }
                  }}
                  style={customStyles.btnImportPremium}>
                  <ConditionalRender
                    condition={import_in_progress}
                    childrenA={
                      <ActivityIndicator
                        size="small"
                        color={CCColors.GameReview.DarkGoldText}
                      />
                    }
                    childrenB={
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                        }}>
                        <MaterialIcons
                          name="sync"
                          size={15}
                          color={CCColors.GameReview.DarkGoldText}
                        />
                        <CCText style={customStyles.btnImportPremiumText}>
                          Import Games
                        </CCText>
                      </View>
                    }
                  />
                </CCButton>
              </View>
            </View>
          </View>
        </View>

        {/* ── Top Navigation & Filters Row ────────────────────────── */}
        <View style={customStyles.controlsRow}>
          {/* Tabs on the Left */}
          <View style={customStyles.bannerTabBar}>
            {(
              [
                {key: 'ALL', label: 'All Reviews'},
                {key: 'FAVORITES', label: 'Favorites'},
              ] as const
            ).map(tab => (
              <Pressable
                key={tab.key}
                onPress={() => setActiveTab(tab.key)}
                style={[
                  customStyles.bannerTabBtn,
                  activeTab === tab.key && customStyles.bannerTabBtnActive,
                ]}>
                <CCText
                  style={[
                    customStyles.bannerTabBtnText,
                    activeTab === tab.key &&
                      customStyles.bannerTabBtnTextActive,
                  ]}>
                  {tab.label}
                </CCText>
              </Pressable>
            ))}
          </View>

          {/* Controls on the Right */}
          <View style={customStyles.bannerFilterGroup}>
            <View style={customStyles.bannerSearchWrapper}>
              <AntDesign
                name="search1"
                size={13}
                color={CCColors.GameReview.Muted}
                style={{marginRight: 7}}
              />
              <CCTextInput
                value={filterText}
                style={[
                  customStyles.searchInput,
                  {
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    borderWidth: 0,
                  },
                ]}
                outlineColor="transparent"
                outlineActiveColor="transparent"
                placeholder="Search opponent name..."
                onChangeText={filterSearchHandler}
                placeholderTextColor={CCColors.GameReview.Muted}
              />
            </View>

            {!filter_date && (
              <Filters
                handleFilter={handleFilter}
                style={customStyles.btnFilters}
              />
            )}

            <CCButton
              onPress={() => {
                navigation.setParams({});
                handleFilter([]);
                setFilters({});
                setFilterText('');
              }}
              style={customStyles.btnReset}>
              <CCText style={customStyles.btnResetText}>✕ Reset</CCText>
            </CCButton>
          </View>
        </View>

        {/* ── Section Label ─────────────────────────────────────────── */}
        <View style={customStyles.sectionLabelRow}>
          <CCText style={customStyles.sectionLabel}>RECENT</CCText>
          <View style={customStyles.sectionLabelLine} />
        </View>

        {/* ── Game Cards ────────────────────────────────────────────── */}
        <View style={customStyles.gamesList}>
          {(() => {
            const filteredGames = games.filter((item: any) => {
              if (activeTab === 'FAVORITES') {
                return favouriteState[item.uuid] === '⭐' || item.is_favourite;
              }
              return true;
            });

            return filteredGames.length > 0 ? (
              filteredGames.map((item: any) => {
                const gameUuid = item.uuid;
                const isFav =
                  favouriteState[gameUuid] === '⭐' || item.is_favourite;

                const handleCardPress = () => {
                  const viewing = item.analyzed === 0 || item.is_analyzing;
                  if (viewing) {
                    navigation.navigate(ScreenName?.ChessAnalysis, {
                      game_id: gameUuid,
                      page: currentPage,
                      viewgame: false,
                      time_control: convertTimeControlToMins(
                        item?.time_control,
                      ),
                      time_class: capitalizeFirstLetter(
                        item?.time_class ?? 'rapid',
                      ),
                      opponent: item.opponent_username,
                      player: item.username,
                      opponent_rating: item.opponent_rating,
                      player_rating: item.rating,
                    });
                  } else {
                    navigation.navigate(ScreenName?.ChessAnalysis, {
                      game_id: gameUuid,
                      analyzed: item.analyzed,
                      page: currentPage,
                      isUser: dashboardData?.user?.is_staff,
                    });
                  }
                };

                return (
                  <GameCard
                    key={gameUuid}
                    item={item}
                    isFav={isFav}
                    onToggleFav={() => markFavourite({game_uuid: gameUuid})}
                    onPress={handleCardPress}
                    navigation={navigation}
                  />
                );
              })
            ) : (
              <View style={customStyles.emptyStateImproved}>
                <CCText style={customStyles.emptyStateIcon}>♚</CCText>
                <CCText style={customStyles.emptyStateTitle}>
                  {activeTab === 'FAVORITES'
                    ? 'No favourites yet'
                    : 'No games found'}
                </CCText>
                <CCText style={customStyles.emptyStateBody}>
                  {activeTab === 'FAVORITES'
                    ? 'Star a game to save it here for quick access.'
                    : 'Import your games from Chess.com or Lichess to begin your analysis journey.'}
                </CCText>
              </View>
            );
          })()}
        </View>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginate={paginate}
        />
      </View>

      <SubscriptionModal
        visible={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        user={dashboardData?.user}
        planMessage={planMessage}
        currentPlan={planName}
      />
    </>
  );
};

export default Games;
